import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import styled from "styled-components";
import {
  ArrowLeft,
  Search,
  Download,
  Edit2,
  Trash2,
  Phone,
  MapPin,
  MessageCircle,
  Coins,
  TrendingUp,
  TrendingDown,
  Clock,
  X,
  Save,
  Calendar
} from "lucide-react";
import API_BASE from "../config/api";
import { exportTradesToCSV } from "../utils/csvExport";
import { ConfirmModal, ToastNotification } from "../components/admin/AdminModals";

const PageContainer = styled.div`
  background-color: #0b0f19;
  color: #f3f4f6;
  min-height: 100vh;
  width: 100%;
  max-width: 100vw;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  padding: 1.5rem;
  overflow-x: hidden;
  box-sizing: border-box;

  *, *::before, *::after {
    box-sizing: border-box;
  }

  @media (max-width: 768px) {
    padding: 0.85rem 0.75rem;
  }
`;

const ContentWrap = styled.div`
  max-width: 1300px;
  width: 100%;
  min-width: 0;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;

  @media (max-width: 640px) {
    gap: 0.85rem;
  }
`;

const TopNav = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.75rem;
  width: 100%;
  min-width: 0;

  @media (max-width: 480px) {
    flex-direction: column;
    align-items: stretch;
    gap: 0.5rem;
  }
`;

const BackBtn = styled.button`
  background: #161f30;
  border: 1px solid #1f293d;
  color: #d1d5db;
  border-radius: 8px;
  padding: 0.5rem 0.85rem;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  transition: all 0.2s;

  &:hover {
    background: #1f293d;
    color: #f59e0b;
    border-color: rgba(245, 158, 11, 0.3);
  }

  @media (max-width: 480px) {
    font-size: 0.82rem;
    padding: 0.5rem;
    justify-content: center;
    width: 100%;
  }
`;

// Responsive User Profile Hero Card
const UserHeroCard = styled.div`
  background: #111827;
  border: 1px solid #1f293d;
  border-radius: 14px;
  padding: 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1.25rem;
  box-shadow: 0 4px 25px rgba(0, 0, 0, 0.3);

  @media (max-width: 768px) {
    padding: 1.15rem 1rem;
    flex-direction: column;
    align-items: stretch;
    border-radius: 10px;
  }
`;

const UserIdentity = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;

  @media (max-width: 480px) {
    gap: 0.75rem;
  }
`;

const Avatar = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 12px;
  background: linear-gradient(135deg, rgba(245, 158, 11, 0.2), rgba(217, 119, 6, 0.3));
  border: 1px solid rgba(245, 158, 11, 0.4);
  color: #f59e0b;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  font-weight: 700;
  flex-shrink: 0;

  @media (max-width: 480px) {
    width: 42px;
    height: 42px;
    font-size: 1.1rem;
  }
`;

const UserDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  overflow: hidden;

  h2 {
    margin: 0;
    font-size: 1.25rem;
    font-weight: 700;
    color: #f9fafb;

    @media (max-width: 480px) {
      font-size: 1.1rem;
    }
  }

  .meta {
    display: flex;
    align-items: center;
    gap: 0.85rem;
    font-size: 0.82rem;
    color: #9ca3af;
    flex-wrap: wrap;

    span {
      display: flex;
      align-items: center;
      gap: 0.35rem;
    }

    a {
      color: #9ca3af;
      transition: color 0.2s;
      &:hover {
        color: #10b981;
      }
    }
  }
`;

const UserKpis = styled.div`
  display: flex;
  align-items: center;
  gap: 0.85rem;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0.6rem;
    width: 100%;
  }
`;

const KpiBox = styled.div`
  background: #0b0f19;
  border: 1px solid #1f293d;
  border-radius: 10px;
  padding: 0.65rem 0.95rem;
  display: flex;
  flex-direction: column;
  min-width: 120px;

  .label {
    font-size: 0.68rem;
    text-transform: uppercase;
    font-weight: 600;
    color: #9ca3af;
    letter-spacing: 0.5px;
    margin-bottom: 0.15rem;
  }

  .val {
    font-size: 1.1rem;
    font-weight: 700;
    color: ${(props) => props.$color || "#f3f4f6"};

    @media (max-width: 480px) {
      font-size: 0.95rem;
    }
  }

  @media (max-width: 768px) {
    min-width: auto;
  }
`;

// Filter Card
const FilterBar = styled.div`
  background: #111827;
  border: 1px solid #1f293d;
  border-radius: 14px;
  padding: 1.1rem 1.4rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.85rem;

  @media (max-width: 640px) {
    padding: 0.85rem;
    border-radius: 10px;
  }
`;

const SearchInputWrap = styled.div`
  position: relative;
  flex: 1;
  min-width: 250px;

  svg {
    position: absolute;
    left: 0.85rem;
    top: 50%;
    transform: translateY(-50%);
    color: #6b7280;
  }

  @media (max-width: 640px) {
    min-width: 100%;
  }
`;

const SearchInput = styled.input`
  width: 100%;
  background: #0b0f19;
  border: 1px solid #1f293d;
  border-radius: 8px;
  padding: 0.6rem 0.85rem 0.6rem 2.4rem;
  color: #f3f4f6;
  font-size: 0.88rem;
  outline: none;
  transition: all 0.2s;

  &:focus {
    border-color: #f59e0b;
    box-shadow: 0 0 0 2px rgba(245, 158, 11, 0.2);
  }

  &::placeholder {
    color: #6b7280;
  }

  @media (max-width: 640px) {
    font-size: 16px;
  }
`;

const ControlsGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;

  @media (max-width: 640px) {
    width: 100%;
  }
`;

const DateInput = styled.input`
  background: #0b0f19;
  border: 1px solid #1f293d;
  color: #d1d5db;
  border-radius: 8px;
  padding: 0.5rem 0.75rem;
  font-size: 0.85rem;
  outline: none;
  cursor: pointer;
  color-scheme: dark;

  &:focus {
    border-color: #f59e0b;
  }

  @media (max-width: 640px) {
    flex: 1;
    font-size: 16px;
  }
`;

const ActionBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.45rem;
  background: #1f293d;
  color: #d1d5db;
  border: 1px solid #2b3648;
  border-radius: 8px;
  padding: 0.55rem 0.85rem;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #374151;
    color: #fff;
  }

  &.export {
    background: rgba(16, 185, 129, 0.15);
    color: #10b981;
    border-color: rgba(16, 185, 129, 0.3);

    &:hover {
      background: rgba(16, 185, 129, 0.25);
    }
  }

  &.reset {
    background: #1f2937;
    color: #9ca3af;
  }

  @media (max-width: 640px) {
    padding: 0.5rem;
    font-size: 0.8rem;
  }
`;

// Table Card
const TableCard = styled.div`
  background: #111827;
  border: 1px solid #1f293d;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 4px 25px rgba(0, 0, 0, 0.25);

  @media (max-width: 640px) {
    border-radius: 10px;
  }
`;

/* 🖥️ Desktop Table */
const DesktopTableWrapper = styled.div`
  overflow-x: auto;

  @media (max-width: 800px) {
    display: none;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  text-align: left;
  font-size: 0.88rem;

  th {
    background: #0e1422;
    padding: 0.9rem 1.25rem;
    font-weight: 600;
    color: #9ca3af;
    text-transform: uppercase;
    font-size: 0.72rem;
    letter-spacing: 0.6px;
    border-bottom: 1px solid #1f293d;
    white-space: nowrap;
  }

  td {
    padding: 0.95rem 1.25rem;
    border-bottom: 1px solid #1a2234;
    color: #d1d5db;
    vertical-align: middle;
  }

  tbody tr:hover {
    background-color: #161f30;
  }
`;

const Badge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.22rem 0.6rem;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;

  &.buy {
    background: rgba(16, 185, 129, 0.15);
    color: #10b981;
    border: 1px solid rgba(16, 185, 129, 0.3);
  }

  &.sell {
    background: rgba(239, 68, 68, 0.15);
    color: #ef4444;
    border: 1px solid rgba(239, 68, 68, 0.3);
  }

  &.gold {
    background: rgba(245, 158, 11, 0.12);
    color: #f59e0b;
    border: 1px solid rgba(245, 158, 11, 0.3);
  }

  &.silver {
    background: rgba(148, 163, 184, 0.15);
    color: #cbd5e1;
    border: 1px solid rgba(148, 163, 184, 0.3);
  }
`;

/* 📱 Mobile Trade Cards (Active on <= 800px) */
const MobileTradesContainer = styled.div`
  display: none;
  padding: 1rem;
  flex-direction: column;
  gap: 0.85rem;

  @media (max-width: 800px) {
    display: flex;
  }
`;

const MobileTradeCard = styled.div`
  background: #161f30;
  border: 1px solid #1f293d;
  border-radius: 12px;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
`;

const MobileTradeHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
`;

const MobileTradeBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  padding: 0.5rem 0;
  border-top: 1px solid #1f293d;
  border-bottom: 1px solid #1f293d;

  .meta-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 0.82rem;
    color: #9ca3af;
  }

  .price-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 0.25rem;
    padding-top: 0.4rem;
    border-top: 1px dashed #1f293d;
  }
`;

const MobileTradeFooter = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem;

  button {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.35rem;
    padding: 0.5rem;
    border-radius: 6px;
    font-size: 0.8rem;
    font-weight: 600;
    border: none;
    cursor: pointer;
  }

  .edit-btn {
    background: #1f293d;
    color: #e5e7eb;
    border: 1px solid #374151;
  }

  .del-btn {
    background: rgba(239, 68, 68, 0.15);
    color: #ef4444;
    border: 1px solid rgba(239, 68, 68, 0.3);
  }
`;

const ActionIconBtn = styled.button`
  background: #1f293d;
  border: 1px solid #2b3648;
  color: #9ca3af;
  border-radius: 6px;
  padding: 0.4rem 0.55rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;

  &:hover {
    background: #374151;
    color: #f3f4f6;
  }

  &.edit:hover {
    color: #f59e0b;
    border-color: #d97706;
  }

  &.delete:hover {
    color: #ef4444;
    border-color: #dc2626;
  }
`;

// Edit Modal
const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.75);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 1rem;
`;

const ModalContent = styled.div`
  background: #111827;
  border: 1px solid #1f293d;
  border-radius: 14px;
  width: 100%;
  max-width: 480px;
  max-height: 90vh;
  overflow-y: auto;
  padding: 1.5rem;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.6);
  color: #fff;

  @media (max-width: 480px) {
    padding: 1.25rem 1rem;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.25rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #1f293d;

  h3 {
    margin: 0;
    font-size: 1.15rem;
    font-weight: 600;
    color: #f3f4f6;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1rem;

  label {
    display: block;
    font-size: 0.78rem;
    font-weight: 600;
    color: #9ca3af;
    margin-bottom: 0.35rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  input, select {
    width: 100%;
    background: #0b0f19;
    border: 1px solid #1f293d;
    border-radius: 8px;
    padding: 0.6rem 0.85rem;
    color: #f3f4f6;
    font-size: 0.9rem;
    outline: none;
    transition: all 0.2s;

    &:focus {
      border-color: #f59e0b;
    }

    @media (max-width: 640px) {
      font-size: 16px;
    }
  }

  select option {
    background: #111827;
  }
`;

const PricePreview = styled.div`
  background: #0b0f19;
  border: 1px dashed rgba(245, 158, 11, 0.4);
  border-radius: 8px;
  padding: 0.85rem;
  margin: 1.25rem 0;
  display: flex;
  justify-content: space-between;
  align-items: center;

  .label {
    font-size: 0.82rem;
    color: #9ca3af;
  }
  .amount {
    font-size: 1.2rem;
    font-weight: 700;
    color: #f59e0b;
  }
`;

const AdminUserTrades = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [trades, setTrades] = useState([]);
  const [userInfo, setUserInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [dateFilter, setDateFilter] = useState("");
  const [toast, setToast] = useState(null);

  // Edit / Delete Modals
  const [editingTrade, setEditingTrade] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [deletingTradeId, setDeletingTradeId] = useState(null);

  const showToast = ({ type, message }) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 4000);
  };

  useEffect(() => {
    const fetchUserAndTrades = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem("adminToken");
        if (!token) {
          navigate("/admin-login");
          return;
        }

        const res = await fetch(`${API_BASE}/api/admin/users/${userId}/trades`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        const data = await res.json();

        if (Array.isArray(data)) {
          setTrades(data);
          if (data.length > 0) {
            setUserInfo({
              name: data[0].fullName,
              phone: data[0].phone,
            });
          }
        }

        const usersRes = await fetch(`${API_BASE}/api/admin/users`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (usersRes.ok) {
          const allUsers = await usersRes.json();
          const found = allUsers.find((u) => u._id === userId);
          if (found) {
            setUserInfo({
              name: found.fullName,
              phone: found.phone,
              address: found.address,
              createdAt: found.createdAt,
            });
          }
        }
      } catch (err) {
        console.error("Failed to fetch user trades:", err);
        showToast({ type: "error", message: "Error fetching customer trades." });
      } finally {
        setLoading(false);
      }
    };

    fetchUserAndTrades();
  }, [userId, navigate]);

  const filteredTrades = useMemo(() => {
    return trades.filter((trade) => {
      if (searchTerm) {
        const q = searchTerm.toLowerCase();
        const match =
          (trade.metal || "").toLowerCase().includes(q) ||
          (trade.action || "").toLowerCase().includes(q) ||
          String(trade.rate || "").includes(q) ||
          String(trade.quantity || "").includes(q) ||
          String(trade.totalPrice || "").includes(q);
        if (!match) return false;
      }

      if (dateFilter) {
        const tradeDate = new Date(trade.timestamp).toISOString().split("T")[0];
        if (tradeDate !== dateFilter) return false;
      }

      return true;
    });
  }, [trades, searchTerm, dateFilter]);

  const stats = useMemo(() => {
    let buyTotal = 0;
    let sellTotal = 0;

    trades.forEach((t) => {
      const amt = Number(t.totalPrice) || 0;
      if (t.action?.toLowerCase() === "buy") buyTotal += amt;
      else if (t.action?.toLowerCase() === "sell") sellTotal += amt;
    });

    return {
      count: trades.length,
      buyTotal,
      sellTotal,
      netVolume: buyTotal + sellTotal,
    };
  }, [trades]);

  const calculateEstimatedTotal = () => {
    if (!editingTrade) return 0;
    const isGold = (editingTrade.metal || "").toLowerCase().includes("gold");
    const numericRate = parseFloat(editingTrade.rate) || 0;
    const numericQty = parseFloat(editingTrade.quantity) || 0;
    const perGramRate = isGold ? numericRate / 10 : numericRate;
    return +(perGramRate * numericQty).toFixed(2);
  };

  const handleUpdateTrade = async (e) => {
    e.preventDefault();
    if (!editingTrade) return;
    setSavingEdit(true);

    try {
      const res = await fetch(`${API_BASE}/api/trades/${editingTrade._id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
        },
        body: JSON.stringify({
          metal: editingTrade.metal,
          action: editingTrade.action,
          rate: editingTrade.rate,
          quantity: editingTrade.quantity,
        }),
      });

      const data = await res.json();
      if (res.ok) {
        setTrades(trades.map((t) => (t._id === editingTrade._id ? data.trade || editingTrade : t)));
        setEditingTrade(null);
        showToast({ type: "success", message: "Trade updated successfully!" });
      } else {
        showToast({ type: "error", message: data.message || "Failed to update trade." });
      }
    } catch (err) {
      console.error("Update error:", err);
      showToast({ type: "error", message: "Update failed." });
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDeleteTrade = async () => {
    if (!deletingTradeId) return;
    try {
      const res = await fetch(`${API_BASE}/api/trades/${deletingTradeId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("adminToken")}`,
        },
      });

      if (res.ok) {
        setTrades(trades.filter((t) => t._id !== deletingTradeId));
        showToast({ type: "success", message: "Trade record deleted." });
      } else {
        showToast({ type: "error", message: "Failed to delete trade." });
      }
    } catch (err) {
      console.error("Delete error:", err);
      showToast({ type: "error", message: "Server connection error." });
    } finally {
      setDeletingTradeId(null);
    }
  };

  const cleanPhone = userInfo?.phone ? userInfo.phone.replace(/[^0-9]/g, "") : "";

  return (
    <PageContainer>
      <ContentWrap>
        {/* 🧭 Top Breadcrumb & Nav */}
        <TopNav>
          <BackBtn onClick={() => navigate("/admin")}>
            <ArrowLeft size={16} />
            Back to Dashboard
          </BackBtn>

          <ActionBtn
            className="export"
            onClick={() =>
              exportTradesToCSV(
                filteredTrades,
                `${userInfo?.name || "Customer"}_Trades_${new Date().toISOString().slice(0, 10)}.csv`
              )
            }
          >
            <Download size={14} /> Export (CSV)
          </ActionBtn>
        </TopNav>

        {/* 👤 Responsive Customer Profile Summary Card */}
        <UserHeroCard>
          <UserIdentity>
            <Avatar>{userInfo?.name ? userInfo.name.charAt(0).toUpperCase() : "U"}</Avatar>
            <UserDetails>
              <h2>{userInfo?.name || "Customer Trade Account"}</h2>
              <div className="meta">
                {userInfo?.phone && (
                  <span>
                    <Phone size={13} color="#9ca3af" />
                    {userInfo.phone}
                    {cleanPhone && (
                      <a
                        href={`https://wa.me/91${cleanPhone}`}
                        target="_blank"
                        rel="noreferrer"
                        title="Chat on WhatsApp"
                      >
                        <MessageCircle size={14} />
                      </a>
                    )}
                  </span>
                )}
                {userInfo?.address && (
                  <span>
                    <MapPin size={13} color="#9ca3af" />
                    {userInfo.address}
                  </span>
                )}
                {userInfo?.createdAt && (
                  <span>
                    <Calendar size={13} color="#9ca3af" />
                    Joined: {new Date(userInfo.createdAt).toLocaleDateString("en-IN")}
                  </span>
                )}
              </div>
            </UserDetails>
          </UserIdentity>

          <UserKpis>
            <KpiBox>
              <span className="label">Total Orders</span>
              <span className="val">{stats.count}</span>
            </KpiBox>
            <KpiBox $color="#10b981">
              <span className="label">Total Buy (₹)</span>
              <span className="val">₹{stats.buyTotal.toLocaleString("en-IN")}</span>
            </KpiBox>
            <KpiBox $color="#ef4444">
              <span className="label">Total Sell (₹)</span>
              <span className="val">₹{stats.sellTotal.toLocaleString("en-IN")}</span>
            </KpiBox>
            <KpiBox $color="#f59e0b">
              <span className="label">Net Volume</span>
              <span className="val">₹{stats.netVolume.toLocaleString("en-IN")}</span>
            </KpiBox>
          </UserKpis>
        </UserHeroCard>

        {/* 🔍 Search & Filters Bar */}
        <FilterBar>
          <SearchInputWrap>
            <Search size={16} />
            <SearchInput
              type="text"
              placeholder="Search by metal, action, rate, qty..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </SearchInputWrap>

          <ControlsGroup>
            <DateInput
              type="date"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
            />
            {(searchTerm || dateFilter) && (
              <ActionBtn
                className="reset"
                onClick={() => {
                  setSearchTerm("");
                  setDateFilter("");
                }}
              >
                <X size={14} /> Clear
              </ActionBtn>
            )}
          </ControlsGroup>
        </FilterBar>

        {/* 🧾 Trades Display */}
        <TableCard>
          {/* 🖥️ Desktop Table View */}
          <DesktopTableWrapper>
            <Table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Date & Time</th>
                  <th>Metal</th>
                  <th>Action</th>
                  <th>Rate</th>
                  <th>Quantity</th>
                  <th>Total Amount</th>
                  <th style={{ textAlign: "right" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="8" style={{ textAlign: "center", padding: "3rem", color: "#9ca3af" }}>
                      Loading trades history...
                    </td>
                  </tr>
                ) : filteredTrades.length === 0 ? (
                  <tr>
                    <td colSpan="8" style={{ textAlign: "center", padding: "3rem", color: "#6b7280" }}>
                      No trades found for this customer.
                    </td>
                  </tr>
                ) : (
                  filteredTrades.map((trade, idx) => {
                    const isGold = (trade.metal || "").toLowerCase().includes("gold");
                    const isBuy = trade.action?.toLowerCase() === "buy";

                    return (
                      <tr key={trade._id || idx}>
                        <td style={{ color: "#6b7280", fontWeight: 500 }}>{idx + 1}</td>
                        <td>
                          <div style={{ display: "flex", flexDirection: "column" }}>
                            <span style={{ color: "#f3f4f6", fontWeight: 500 }}>
                              {new Date(trade.timestamp).toLocaleDateString("en-IN", {
                                day: "2-digit",
                                month: "short",
                                year: "numeric",
                              })}
                            </span>
                            <span style={{ color: "#6b7280", fontSize: "0.75rem", display: "flex", alignItems: "center", gap: "0.25rem" }}>
                              <Clock size={11} />
                              {new Date(trade.timestamp).toLocaleTimeString("en-IN", {
                                hour: "2-digit",
                                minute: "2-digit",
                              })}
                            </span>
                          </div>
                        </td>
                        <td>
                          <Badge className={isGold ? "gold" : "silver"}>
                            <Coins size={12} />
                            {trade.metal}
                          </Badge>
                        </td>
                        <td>
                          <Badge className={isBuy ? "buy" : "sell"}>
                            {isBuy ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                            {trade.action?.toUpperCase()}
                          </Badge>
                        </td>
                        <td style={{ fontWeight: 600, color: "#f3f4f6" }}>
                          ₹{Number(trade.rate || 0).toLocaleString("en-IN")}
                        </td>
                        <td style={{ fontWeight: 600, color: "#d1d5db" }}>{trade.quantity}</td>
                        <td>
                          <span style={{ fontWeight: 700, color: isBuy ? "#10b981" : "#f87171", fontSize: "0.95rem" }}>
                            ₹{Number(trade.totalPrice || 0).toLocaleString("en-IN")}
                          </span>
                        </td>
                        <td style={{ textAlign: "right" }}>
                          <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.4rem" }}>
                            <ActionIconBtn
                              className="edit"
                              title="Edit Trade"
                              onClick={() => setEditingTrade({ ...trade })}
                            >
                              <Edit2 size={13} />
                            </ActionIconBtn>
                            <ActionIconBtn
                              className="delete"
                              title="Delete Trade"
                              onClick={() => setDeletingTradeId(trade._id)}
                            >
                              <Trash2 size={13} />
                            </ActionIconBtn>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </Table>
          </DesktopTableWrapper>

          {/* 📱 Mobile Trade Cards View (Adaptive on <= 800px) */}
          <MobileTradesContainer>
            {loading ? (
              <div style={{ textAlign: "center", padding: "2rem", color: "#9ca3af" }}>
                Loading customer trades...
              </div>
            ) : filteredTrades.length === 0 ? (
              <div style={{ textAlign: "center", padding: "2rem", color: "#6b7280" }}>
                No trades found for this customer.
              </div>
            ) : (
              filteredTrades.map((trade, idx) => {
                const isGold = (trade.metal || "").toLowerCase().includes("gold");
                const isBuy = trade.action?.toLowerCase() === "buy";

                return (
                  <MobileTradeCard key={trade._id || idx}>
                    <MobileTradeHeader>
                      <div style={{ display: "flex", alignItems: "center", gap: "0.4rem", color: "#9ca3af", fontSize: "0.78rem" }}>
                        <span style={{ color: "#6b7280", fontWeight: 600 }}>#{idx + 1}</span>
                        <span>•</span>
                        <span>
                          {new Date(trade.timestamp).toLocaleDateString("en-IN", {
                            day: "2-digit",
                            month: "short",
                          })}
                          , {new Date(trade.timestamp).toLocaleTimeString("en-IN", {
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </span>
                      </div>
                      <Badge className={isBuy ? "buy" : "sell"}>
                        {isBuy ? <TrendingUp size={11} /> : <TrendingDown size={11} />}
                        {trade.action?.toUpperCase()}
                      </Badge>
                    </MobileTradeHeader>

                    <MobileTradeBody>
                      <div className="meta-row">
                        <Badge className={isGold ? "gold" : "silver"}>
                          <Coins size={11} />
                          {trade.metal}
                        </Badge>
                        <span style={{ color: "#d1d5db", fontWeight: 600 }}>
                          Qty: {trade.quantity}
                        </span>
                      </div>

                      <div className="price-row">
                        <span style={{ color: "#9ca3af", fontSize: "0.8rem" }}>
                          Rate: ₹{Number(trade.rate || 0).toLocaleString("en-IN")}
                        </span>
                        <span style={{ fontWeight: 800, color: isBuy ? "#10b981" : "#f87171", fontSize: "1.05rem" }}>
                          ₹{Number(trade.totalPrice || 0).toLocaleString("en-IN")}
                        </span>
                      </div>
                    </MobileTradeBody>

                    <MobileTradeFooter>
                      <button className="edit-btn" onClick={() => setEditingTrade({ ...trade })}>
                        <Edit2 size={13} /> Edit Trade
                      </button>
                      <button className="del-btn" onClick={() => setDeletingTradeId(trade._id)}>
                        <Trash2 size={13} /> Delete
                      </button>
                    </MobileTradeFooter>
                  </MobileTradeCard>
                );
              })
            )}
          </MobileTradesContainer>
        </TableCard>

        {/* ✏️ Edit Trade Modal */}
        {editingTrade && (
          <ModalOverlay onClick={() => setEditingTrade(null)}>
            <ModalContent onClick={(e) => e.stopPropagation()}>
              <ModalHeader>
                <h3>
                  <Edit2 size={18} color="#f59e0b" />
                  Modify Trade Record
                </h3>
                <button
                  onClick={() => setEditingTrade(null)}
                  style={{ background: "none", border: "none", color: "#9ca3af", cursor: "pointer" }}
                >
                  <X size={18} />
                </button>
              </ModalHeader>

              <form onSubmit={handleUpdateTrade}>
                <FormGroup>
                  <label>Metal Name</label>
                  <input
                    type="text"
                    required
                    value={editingTrade.metal || ""}
                    onChange={(e) => setEditingTrade({ ...editingTrade, metal: e.target.value })}
                  />
                </FormGroup>

                <FormGroup>
                  <label>Action</label>
                  <select
                    value={editingTrade.action?.toLowerCase() || "buy"}
                    onChange={(e) => setEditingTrade({ ...editingTrade, action: e.target.value })}
                  >
                    <option value="buy">BUY</option>
                    <option value="sell">SELL</option>
                  </select>
                </FormGroup>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.75rem" }}>
                  <FormGroup>
                    <label>Rate (₹)</label>
                    <input
                      type="number"
                      step="any"
                      required
                      value={editingTrade.rate || ""}
                      onChange={(e) => setEditingTrade({ ...editingTrade, rate: e.target.value })}
                    />
                  </FormGroup>

                  <FormGroup>
                    <label>Quantity</label>
                    <input
                      type="text"
                      required
                      value={editingTrade.quantity || ""}
                      onChange={(e) => setEditingTrade({ ...editingTrade, quantity: e.target.value })}
                    />
                  </FormGroup>
                </div>

                <PricePreview>
                  <span className="label">Estimated Total:</span>
                  <span className="amount">₹{calculateEstimatedTotal().toLocaleString("en-IN")}</span>
                </PricePreview>

                <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.75rem", marginTop: "1rem" }}>
                  <button
                    type="button"
                    onClick={() => setEditingTrade(null)}
                    style={{
                      background: "#1f2937",
                      color: "#e5e7eb",
                      border: "1px solid #374151",
                      borderRadius: "8px",
                      padding: "0.6rem 1.1rem",
                      cursor: "pointer",
                      fontSize: "0.88rem",
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={savingEdit}
                    style={{
                      background: "linear-gradient(135deg, #f59e0b, #d97706)",
                      color: "#0b0f19",
                      fontWeight: 600,
                      border: "none",
                      borderRadius: "8px",
                      padding: "0.6rem 1.25rem",
                      cursor: savingEdit ? "not-allowed" : "pointer",
                      fontSize: "0.88rem",
                      display: "flex",
                      alignItems: "center",
                      gap: "0.4rem",
                    }}
                  >
                    <Save size={15} />
                    {savingEdit ? "Updating..." : "Update Trade"}
                  </button>
                </div>
              </form>
            </ModalContent>
          </ModalOverlay>
        )}

        {/* 🗑️ Delete Trade Confirmation */}
        <ConfirmModal
          isOpen={Boolean(deletingTradeId)}
          title="Delete Trade Record"
          message="Are you sure you want to delete this customer transaction? This cannot be undone."
          confirmText="Yes, Delete Trade"
          isDanger={true}
          onConfirm={handleDeleteTrade}
          onCancel={() => setDeletingTradeId(null)}
        />

        {/* 🔔 Toast Notification */}
        <ToastNotification toast={toast} onClose={() => setToast(null)} />
      </ContentWrap>
    </PageContainer>
  );
};

export default AdminUserTrades;
